import React from 'react';
import { Link } from 'react-router-dom';
import AdvancedDataTable from '../../../DataTables/AdvancedDataTable';
import StatusWidget from '../../../StatusWidget/StatusWidget';
import convertInchesToFeetAndInches from '../../../../utils/convertInchesToFeetAndInches';
import GradesValueToColorHelper from '../../../Helpers/GradesValueToColorHelper';

const DraftTrackerPlayersTable = ({
	players,
	roster,
	sortBy,
	sortOrder,
	handleSortOrderChange,
	isDraftModeEnabled,
	selectedPlayers,
	handlePlayerSelect,
	handlePlayerHideFromList,
	handlePlayerModalOpen,
	isHeatmapEnabled,
	isHeatmapFFScoresCategoryEnabled
}) => {
	return (
		<div>
			<AdvancedDataTable
				data={players}
				style="text-gray-800 border-b border-gray-300 font-semibold"
				sortingEnabled={true}
				sortOrder={sortOrder}
				sortColumn={sortBy ? sortBy : ''}
				onChangeSortOrder={handleSortOrderChange}
				columns={[
					{
						displayName: 'Rank',
						apiName: 'rank',
						alignment: 'left'
					},
					{
						displayName: 'Player',
						apiName: 'player',
						alignment: 'left',
						minWidth: '240px'
					},

					// Add 'Underdog ADP' column with condition
					...(isDraftModeEnabled &&
					roster.league &&
					roster.league.platform === 'Underdog'
						? [
								{
									displayName: 'Underdog ADP',
									apiName: 'underdog_adp',
									alignment: 'left'
								}
							]
						: []),

					// Add 'FFPC ADP' column with condition
					...(isDraftModeEnabled &&
					roster.league &&
					roster.league.platform === 'FFPC'
						? [
								{
									displayName: 'FFPC ADP',
									apiName: 'ffpc_adp',
									alignment: 'left'
								}
							]
						: []),

					// Add 'Yahoo! ADP' column with condition
					...(isDraftModeEnabled &&
					roster.league &&
					roster.league.platform === 'Yahoo'
						? [
								{
									displayName: 'Yahoo! ADP',
									apiName: 'yahoo_adp',
									alignment: 'left'
								}
							]
						: []),

					// Add 'ESPN ADP' column with condition
					...(isDraftModeEnabled &&
					roster.league &&
					roster.league.platform === 'ESPN'
						? [
								{
									displayName: 'ESPN ADP',
									apiName: 'espn_adp',
									alignment: 'left'
								}
							]
						: []),

					// Add 'NFL.com column with condition
					...(isDraftModeEnabled &&
					roster.league &&
					roster.league.platform === 'NFL'
						? [
								{
									displayName: 'NFL ADP',
									apiName: 'nfl_adp',
									alignment: 'left'
								}
							]
						: []),

					// Add 'Value' column with condition
					...(isDraftModeEnabled
						? [
								{
									displayName: 'Draft Value',
									apiName: 'draft_value',
									alignment: 'left'
								}
							]
						: []),

					{
						displayName: 'Team Score™',
						apiName: 'team_grade',
						alignment: 'left'
					},
					{
						displayName: 'Talent Score™',
						apiName: 'talent_grade',
						alignment: 'left'
					},
					{
						displayName: 'Overall Score™',
						apiName: 'fantasy_fitness_grade',
						alignment: 'left'
					},

					// Add 'Draft Actions' column with condition
					...(isDraftModeEnabled
						? [
								{
									displayName: 'Draft Actions',
									apiName: 'draft',
									alignment: 'center'
								}
							]
						: [])
				]}
				rowRenderer={(row) => {
					const isDrafted = selectedPlayers.some(
						(player) => player.id === row.id && player.position === row.position
					);

					// Hide row if player is drafted
					if (isDrafted) {
						return null;
					}

					return (
						<tr
							className="pl-8 py-3 text-left sticky left-0 bg-white z-10 w-60 border-t border-gray-200"
							key={`${row.first_name} ${row.last_name} - PlayersProfileStatsTable - AdvancedDataTable`}
						>
							<td className="pl-8 py-3 text-left sticky left-0 bg-white z-10">
								1
							</td>
							{/* Rendering Player's name and link */}
							<td className="pl-8 py-3 text-left sticky left-0 bg-white z-10 w-60">
								<div>
									<div
										onClick={() => handlePlayerModalOpen(row)}
										className="text-blue-500 hover:underline cursor-pointer"
									>
										<div
											className={`${isDrafted ? 'line-through' : ''}`}
											style={{ display: 'flex', alignItems: 'center' }}
										>
											<div
												style={{
													width: '50px',
													height: '50px',
													overflow: 'hidden',
													marginRight: '8px'
												}}
											>
												<img
													src={row.image_url + '?w=50&h=50'}
													alt={`${row.first_name} ${row.last_name}`}
													style={{
														width: '50px',
														height: '50px',
														minWidth: '50px',
														minHeight: '50px',
														objectFit: 'cover',
														border: '1px solid #ddd',
														borderRadius: '50%'
													}}
												/>
												{/* I'm not sure I like the status dot on a small thumbnail or on the index page. It looks crowded/busy. Hiding for now. 
														<div
															style={{
																position: 'absolute',
																top: '15px',
																left: '70px'
															}}
														>
															<StatusWidget
																status={row.status}
																description={row.injury_description}
																hideLabel={true}
																is50pXThumbnail={true}
															/>
														</div>
														*/}
											</div>
											{`${row.first_name} ${row.last_name}`}
										</div>
									</div>
								</div>
							</td>

							{/* Rendering Underdog ADP column */}
							{isDraftModeEnabled &&
							roster.league &&
							roster.league.platform === 'Underdog' && (
								<td className="pl-8 py-3 text-left">
									{row.underdog_adp ? row.underdog_adp : '-'}
								</td>
							)}

							{/* Rendering FFPC ADP column */}
							{isDraftModeEnabled &&
							roster.league &&
							roster.league.platform === 'FFPC' && (
								<td className="pl-8 py-3 text-left">
									{row.ffpc_adp ? row.ffpc_adp : '-'}
								</td>
							)}

							{/* Rendering Yahoo ADP column */}
							{isDraftModeEnabled &&
							roster.league &&
							roster.league.platform === 'Yahoo' && (
								<td className="pl-8 py-3 text-left">
									{row.yahoo_adp ? row.yahoo_adp : '-'}
								</td>
							)}

							{/* Rendering ESPN ADP column */}
							{isDraftModeEnabled &&
							roster.league &&
							roster.league.platform === 'ESPN' && (
								<td className="pl-8 py-3 text-left">
									{row.espn_adp ? row.espn_adp : '-'}
								</td>
							)}

							{/* Rendering NFL ADP column */}
							{isDraftModeEnabled &&
							roster.league &&
							roster.league.platform === 'NFL' && (
								<td className="pl-8 py-3 text-left">
									{row.nfl_adp ? row.nfl_adp : '-'}
								</td>
							)}

							{/* Rendering ADP Value column */}
							{isDraftModeEnabled && (
								<td className="pl-8 py-3 text-left">{false ? '5' : '-'}</td>
							)}

							{/* Rendering Team Score column */}
							<td>
								<div
									className="pl-8 py-1 text-left relative"
									style={{
										height: '75px',
										padding: '5px',
										boxSizing: 'border-box'
									}}
								>
									{/* Background color */}
									<div
										style={{
											backgroundColor:
												isHeatmapEnabled && isHeatmapFFScoresCategoryEnabled
													? GradesValueToColorHelper(row.team_grade)
													: 'transparent',
											borderRadius: '10px',
											opacity: isHeatmapEnabled ? '0.7' : '1',
											height: '100%',
											width: '100%'
										}}
									/>

									{/* Text centered over the colored area */}
									<span
										className="text-black"
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											zIndex: 2
										}}
									>
										{row.team_grade}
									</span>
								</div>
							</td>

							{/* Rendering Talent Score column */}
							<td>
								<div
									className="pl-8 py-1 text-left relative"
									style={{
										height: '75px',
										padding: '5px',
										boxSizing: 'border-box'
									}}
								>
									{/* Background color */}
									<div
										style={{
											backgroundColor:
												isHeatmapEnabled && isHeatmapFFScoresCategoryEnabled
													? GradesValueToColorHelper(row.talent_grade)
													: 'transparent',
											borderRadius: '10px',
											opacity: isHeatmapEnabled ? '0.7' : '1',
											height: '100%',
											width: '100%'
										}}
									/>

									{/* Text centered over the colored area */}
									<span
										className="text-black"
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											zIndex: 2
										}}
									>
										{row.talent_grade}
									</span>
								</div>
							</td>

							{/* Rendering Overall Score column */}
							<td>
								<div
									className="pl-8 py-1 text-left relative"
									style={{
										height: '75px',
										padding: '5px',
										boxSizing: 'border-box'
									}}
								>
									{/* Background color */}
									<div
										style={{
											backgroundColor:
												isHeatmapEnabled && isHeatmapFFScoresCategoryEnabled
													? GradesValueToColorHelper(row.fantasy_fitness_grade)
													: 'transparent',
											borderRadius: '10px',
											opacity: isHeatmapEnabled ? '0.7' : '1',
											height: '100%',
											width: '100%'
										}}
									/>

									{/* Text centered over the colored area */}
									<span
										className="text-black"
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											zIndex: 2
										}}
									>
										{row.fantasy_fitness_grade}
									</span>
								</div>
							</td>

							{/* Rendering Draft buttons column */}
							{isDraftModeEnabled && (
								<td>
									<button
										className={`my-0.5 px-3 py-1.5 w-full text-center cursor-pointer border border-b-1 rounded-xl text-xs ${isDrafted
											? ''
											: 'bg-green-500 text-white hover:bg-green-600'}`}
										onClick={() =>
											isDrafted
												? handlePlayerHideFromList(row.id, row.position)
												: handlePlayerSelect(row, true)}
									>
										Drafted by me
									</button>

									<button
										className={`my-0.5 px-3 py-1.5 w-full text-center cursor-pointer border border-b-1 rounded-xl text-xs ${isDrafted
											? ''
											: 'bg-gray-300 text-gray-800 hover:bg-gray-400'}`}
										onClick={() =>
											isDrafted
												? handlePlayerHideFromList(row.id, row.position)
												: handlePlayerSelect(row, false)}
									>
										Drafted by the field
									</button>
								</td>
							)}
						</tr>
					);
				}}
			/>
		</div>
	);
};

export default DraftTrackerPlayersTable;
